import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, MessageCircle, Loader2, Search, AlertCircle, Check } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { usePreferences } from '../context/PreferencesContext';

interface BuddyStatus {
  status: 'none' | 'matching' | 'paired';
  trackSlug?: string;
  joinedAt?: string;
  pairId?: string;
  pairStatus?: string;
  pairedAt?: string;
  unreadMessages?: number;
  buddyLastActive?: string;
}

interface JoinResponse {
  status: 'matched' | 'waiting';
  pairId?: string;
  trackSlug?: string;
  message: string;
}

interface BuddyWidgetProps {
  currentTrackSlug?: string;
}

export default function BuddyWidget({ currentTrackSlug }: BuddyWidgetProps) {
  const { preferences } = usePreferences();
  const { fetchApi } = useApi();
  const [status, setStatus] = useState<BuddyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show for buddy mode
  if (preferences.connection_mode !== 'buddy') {
    return null;
  }

  useEffect(() => {
    loadBuddyStatus();
  }, []);

  async function loadBuddyStatus() {
    try {
      const response = await fetchApi<BuddyStatus>('/api/buddy/status');
      setStatus(response);
    } catch (err) {
      console.error('Failed to load buddy status:', err);
      setError('Unable to load buddy status');
    } finally {
      setLoading(false);
    }
  }

  async function handleJoinPool() {
    if (!currentTrackSlug) {
      setError('Select a track first to find a buddy');
      return;
    }

    setJoining(true);
    setError(null);

    try {
      const response = await fetchApi<JoinResponse>('/api/buddy/join', {
        method: 'POST',
        body: JSON.stringify({ trackSlug: currentTrackSlug })
      });

      if (response.status === 'matched') {
        setStatus({
          status: 'paired',
          pairId: response.pairId,
          trackSlug: response.trackSlug,
          pairedAt: new Date().toISOString(),
          unreadMessages: 1
        });
      } else {
        setStatus({
          status: 'matching',
          trackSlug: response.trackSlug,
          joinedAt: new Date().toISOString()
        });
      }
    } catch (err) {
      setError('Failed to join buddy pool');
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    if (!confirm('Are you sure you want to leave?')) return;

    try {
      await fetchApi('/api/buddy/leave', { method: 'DELETE' });
      setStatus({ status: 'none' });
    } catch (err) {
      setError('Failed to leave');
    }
  }

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  // Not in pool or paired - show join option
  if (!status || status.status === 'none') {
    return (
      <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 border border-green-500/20 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-semibold text-white">Accountability Buddy</h3>
        </div>

        <p className="text-sm text-gray-300 mb-4">
          Get matched with another learner studying the same track. Support each other with async messages and weekly check-ins.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <button
          onClick={handleJoinPool}
          disabled={joining || !currentTrackSlug}
          className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {joining ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Finding buddy...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Find a Buddy
            </>
          )}
        </button>

        {!currentTrackSlug && (
          <p className="text-xs text-gray-500 mt-2 text-center">
            Start a track to find a buddy studying the same content
          </p>
        )}
      </div>
    );
  }

  // In matching pool
  if (status.status === 'matching') {
    return (
      <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border border-yellow-500/20 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-yellow-400" />
          <h3 className="text-lg font-semibold text-white">Finding Your Buddy</h3>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
          </div>
          <div>
            <p className="text-white font-medium">Looking for a match...</p>
            <p className="text-sm text-gray-400">
              We'll notify you when we find someone studying the same track
            </p>
          </div>
        </div>

        <button
          onClick={handleLeave}
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          Leave pool
        </button>
      </div>
    );
  }

  // Paired with buddy
  return (
    <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 border border-green-500/20 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="w-5 h-5 text-green-400" />
        <h3 className="text-lg font-semibold text-white">Your Buddy</h3>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
          <Check className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <p className="text-white font-medium">Matched!</p>
          <p className="text-sm text-gray-400">
            Studying together since {status.pairedAt ? new Date(status.pairedAt).toLocaleDateString() : 'recently'}
          </p>
        </div>
      </div>

      {status.unreadMessages && status.unreadMessages > 0 && (
        <div className="mb-4 p-3 bg-accent-blue/10 border border-accent-blue/30 rounded-lg">
          <p className="text-accent-blue text-sm flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            {status.unreadMessages} new message{status.unreadMessages > 1 ? 's' : ''}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Link
          to="/buddy/chat"
          className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          Messages
        </Link>
        <button
          onClick={handleLeave}
          className="py-2 px-4 text-gray-400 hover:text-white transition-colors text-sm"
        >
          Unmatch
        </button>
      </div>
    </div>
  );
}
